import React from 'react';
import { ArrowLeftIcon, CalendarIcon, EyeOffIcon, MailIcon, MapPinIcon, MessageSquareTextIcon, PhoneIcon } from 'lucide-react';
import { MilestonePill } from '../components/MilestonePill';
import { MilestoneTracker } from '../components/MilestoneTracker';
import type { Loan } from '../types/portal';
interface LoanDetailProps {
  loan: Loan;
  onBack: () => void;
  onAsk: (loan: Loan) => void;
  onEscalate: (loan: Loan) => void;
}
const initials = (name: string) => name.split(' ').map(n => n[0]).join('');
export function LoanDetail({
  loan,
  onBack,
  onAsk,
  onEscalate
}: LoanDetailProps) {
  const keyDates = [{
    label: 'Contract closing date',
    value: loan.contractClose
  }, {
    label: 'Appraisal ordered',
    value: loan.appraisalOrdered ?? 'Not yet ordered'
  }, {
    label: 'Appraisal received',
    value: loan.appraisalReceived ?? 'Pending'
  }, {
    label: loan.status === 'closed' ? 'Closed' : 'Est. closing',
    value: loan.estClose
  }, {
    label: 'Pre-approval expires',
    value: loan.preApprovalExpires
  }];
  return <div className="mx-auto max-w-[1400px]" data-id="element-159" data-mp="31ofhs4c20bbi">
      <button onClick={onBack} className="group inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:text-slate-900 focus:outline-none focus-visible:underline" data-id="element-160" data-mp="2xlr67ovygqau">
        <ArrowLeftIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-161" data-mp="3latxzpkazzj2" />
        My Files
      </button>

      <header className="mt-5 flex flex-wrap items-end justify-between gap-6" data-id="element-162" data-mp="3gcio5eu0dp0u">
        <div data-id="element-163" data-mp="3bk8srja4jwr6">
          <div className="flex items-center gap-3" data-id="element-164" data-mp="3bjpj0pumoeo8">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900" data-id="element-165" data-mp="36mgxcfa9tsi8">{loan.buyer}</h1>
            <MilestonePill milestone={loan.milestone} data-id="element-166" data-mp="7vxdltlqanvm" />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600" data-id="element-167" data-mp="7y9zuiyirkeh">
            <span className="inline-flex items-center gap-1.5" data-id="element-168" data-mp="33npmj46bxvz">
              <MapPinIcon className="h-4 w-4 text-slate-400" data-id="element-169" data-mp="1xem2uyp9ldsp" />
              {loan.street}, {loan.city}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-170" data-mp="21e4ro5k7i90t">
              <CalendarIcon className="h-4 w-4 text-slate-400" data-id="element-171" data-mp="1nmgf6161pijb" />
              {loan.status === 'closed' ? 'Closed' : 'Est. closing'}{' '}
              <span className="font-semibold text-slate-900" data-id="element-172" data-mp="1skhtk8sj0tog">{loan.estClose}</span>
            </span>
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700" data-id="element-173" data-mp="1drg3j24ltkbr">Purchase</span>
          </div>
        </div>
        <div className="flex items-center gap-4" data-id="element-174" data-mp="14pf727h732b5">
          <button onClick={() => onEscalate(loan)} className="text-sm font-medium text-slate-600 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:text-slate-900 focus:outline-none focus-visible:underline" data-id="element-175" data-mp="14qqb88ejyc2s">
            Escalate to leadership
          </button>
          <button onClick={() => onAsk(loan)} className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-brand-dark focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/30 hover:-translate-y-px hover:shadow-md active:translate-y-0 disabled:hover:translate-y-0 disabled:hover:shadow-none" data-id="element-176" data-mp="3e56d56x6bs5i">
            <MessageSquareTextIcon className="h-4 w-4" data-id="element-177" data-mp="3rxctat0btz4y" />
            Ask about this file
          </button>
        </div>
      </header>

      <section aria-label="Milestones" style={{
      '--d': '40ms'
    } as React.CSSProperties} className="animate-fade-up mt-8 rounded-2xl border border-slate-200 bg-white px-6 py-8" data-id="element-178" data-mp="9q6plzv56lo7">
        <MilestoneTracker loan={loan} data-id="element-179" data-mp="2bi2z73xr1lgc" />
      </section>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]" data-id="element-180" data-mp="2bko3dzlisdh9">
        <div className="space-y-6" data-id="element-181" data-mp="27jm1fb07xnbl">
          <section className="rounded-2xl border border-slate-200 bg-white p-6" data-id="element-182" data-mp="28yy9f9lg6xlq">
            <h2 className="text-base font-semibold text-slate-900" data-id="element-183" data-mp="2dx9gjckcyu09">Key dates</h2>
            <dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-3" data-id="element-184" data-mp="2dzcfwn7ehzhn">
              {keyDates.map(d => <div key={d.label} data-id="element-185" data-mp="2mxxu665uascm">
                  <dt className="text-xs text-slate-500" data-id="element-186" data-mp="2mwwko64h2dx0">{d.label}</dt>
                  <dd className="mt-1 text-sm font-semibold tabular-nums text-slate-900" data-id="element-187" data-mp="1q8gqfwqf3gdk">{d.value}</dd>
                </div>)}
            </dl>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6" data-id="element-188" data-mp="1z8lo3f5t70ej">
            <h2 className="text-base font-semibold text-slate-900" data-id="element-189" data-mp="1z6ip45mohwxo">Recent updates</h2>
            <ol className="mt-5" data-id="element-190" data-mp="240mw2ovy24yr">
              {loan.updates.map((u, i) => <li key={u.date + u.text} className="animate-fade-up relative flex gap-4 pb-6 last:pb-0" style={{
              '--d': `${120 + Math.min(i, 6) * 40}ms`
            } as React.CSSProperties} data-id="element-191" data-mp="16j6emttslfkq" data-mpi={i}>
                  {i < loan.updates.length - 1 && <span className="absolute left-[5px] top-4 h-full w-px bg-slate-200" aria-hidden="true" data-id="element-192" data-mp="13x5ol4i67qyi" />}
                  <span className={`relative mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full border-2 ${i === 0 ? 'border-accent bg-accent' : 'border-slate-300 bg-white'}`} aria-hidden="true" data-id="element-193" data-mp="u0suw6mmld3r" />
                  <div className="flex flex-1 items-baseline justify-between gap-4" data-id="element-194" data-mp="ga77c4m88ezj">
                    <p className="text-sm text-slate-800" data-id="element-195" data-mp="baudgzwxu7ng">{u.text}</p>
                    <time className="shrink-0 text-xs tabular-nums text-slate-500" data-id="element-196" data-mp="bb432me2mcsm">{u.date}</time>
                  </div>
                </li>)}
            </ol>
          </section>
        </div>

        <div className="space-y-6" data-id="element-197" data-mp="2c8wh97470os">
          <section className="rounded-2xl border border-slate-200 bg-white p-6" data-id="element-198" data-mp="3tfxloti7azs3">
            <h2 className="text-base font-semibold text-slate-900" data-id="element-199" data-mp="3jsv2j9z2qiyq">Your team on this file</h2>
            <ul className="mt-4 divide-y divide-slate-100" data-id="element-200" data-mp="3jv7p60fs1hi4">
              {loan.team.map(m => <li key={m.role} className="flex items-center gap-3 py-3.5 first:pt-0 last:pb-0" data-id="element-201" data-mp="39unmqugiu6yb">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-semibold text-brand" data-id="element-202" data-mp="3etqskh4bsh7c">
                    {initials(m.name)}
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-203" data-mp="bcs3ttpxw4mj">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-204" data-mp="bciabw5orfc3">{m.name}</p>
                    <p className="text-xs text-slate-500" data-id="element-205" data-mp="3ssqgfmk0ks2g">{m.role}</p>
                  </div>
                  <a href={`tel:${m.phone}`} aria-label={`Call ${m.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:border-slate-300 hover:text-slate-900 hover:-translate-y-px hover:shadow-sm active:translate-y-0" data-id="element-206" data-mp="1m6yzojoa3v6">
                    <PhoneIcon className="h-4 w-4" data-id="element-207" data-mp="2nm8reey6uejb" />
                  </a>
                  <a href={`mailto:${m.email}`} aria-label={`Email ${m.name}`} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:border-slate-300 hover:text-slate-900 hover:-translate-y-px hover:shadow-sm active:translate-y-0" data-id="element-208" data-mp="2nlgtgxh4c4pv">
                    <MailIcon className="h-4 w-4" data-id="element-209" data-mp="1zwee5w9yinyj" />
                  </a>
                </li>)}
            </ul>
          </section>

          <aside className="flex gap-3 rounded-2xl bg-slate-100 p-5 text-sm text-slate-600" data-id="element-210" data-mp="3gd0l95rs6r3h">
            <EyeOffIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" data-id="element-211" data-mp="3bbl0letimmlm" />
            <p data-id="element-212" data-mp="3vf8nyer1t44m">Not shown here: income, assets, credit, rate, loan amount, fees, conditions or borrower documents.</p>
          </aside>
        </div>
      </div>
    </div>;
}